/**
 * Turns a search's per-vendor rows into what the results pane tells the user:
 * how many rates came from where, what did not and why, and what to try next.
 *
 * A search is many rows (vendor x mode x airport/port pair), and several often
 * end the same way (a vendor that does not serve an airport fails every lane
 * from it), so rows are grouped by outcome and list their routes once instead
 * of repeating one banner per lane. Wording comes from the row's `kind`; older
 * backends send none, so a kind is guessed from the status.
 */
import type {
  LaneMode,
  LaneSearch,
  VendorOutcomeKind,
  VendorSearch,
} from '../../../apis/lanePlanner';
import { MODE_TITLES } from './quoteView';

export interface NoticeGroup {
  key: string;
  vendor: string;
  vendorName: string;
  mode: LaneMode;
  status: VendorSearch['status'];
  kind: VendorOutcomeKind | 'pending';
  /** The backend's own sentence for this outcome, when it has one. */
  message: string | null;
  /** "JFK → AMS" for each lane in the group (empty when the rows have no lane). */
  routes: string[];
  /** Why a vendor had no rates on the route at the same index, when it said (a lane it does not serve). */
  reasons: Array<string | null>;
  /** How many lanes this vendor and mode searched in all, to say "3 of 9". */
  laneTotal: number;
  /** Rows to retry; empty when retrying cannot help. */
  retryIds: string[];
  /** This vendor and mode returned rates on other lanes, so the group is a partial miss. */
  rated: boolean;
}

export interface RateSource {
  label: string;
  count: number;
}

export interface SearchReport {
  /** Rates per vendor and mode that returned some. */
  sources: RateSource[];
  /** Everything that did not deliver rates, grouped by outcome. */
  groups: NoticeGroup[];
  /** Inputs a vendor could not apply, each with the vendor it came from. */
  warnings: string[];
}

const modeLabel = (mode: LaneMode): string => MODE_TITLES[mode];

/** "Cargo One (Flight)": how a vendor and mode are named everywhere in the report. */
const sourceLabel = (name: string, mode: LaneMode): string =>
  `${name} (${modeLabel(mode)})`;

const routeText = (row: VendorSearch): string | null =>
  row.route ? `${row.route.origin} → ${row.route.destination}` : null;

/** The row's kind, or a guess from its status for a backend that sends none. */
const kindOf = (row: VendorSearch): NoticeGroup['kind'] | null => {
  if (row.status === 'pending') return 'pending';
  if (row.kind) return row.kind;
  if (row.status === 'unsupported') return 'unsupported';
  if (row.status === 'failed') return 'error';
  return null;
};

/** Where trying again can help: timeouts, outages and our own errors, not a refusal or a login. */
const RETRYABLE_KINDS: ReadonlyArray<NoticeGroup['kind']> = [
  'timeout',
  'unavailable',
  'error',
];

const retryable = (row: VendorSearch): boolean =>
  row.status === 'failed' &&
  (row.retryable ?? RETRYABLE_KINDS.includes(kindOf(row) ?? 'error'));

export const buildSearchReport = (search: LaneSearch): SearchReport => {
  const quoteCounts = new Map<string, number>();
  for (const quote of search.quotes) {
    const key = `${quote.vendor}|${quote.mode}`;
    quoteCounts.set(key, (quoteCounts.get(key) ?? 0) + 1);
  }

  const lanesOf = new Map<string, number>();
  for (const row of search.vendors) {
    const key = `${row.vendor}|${row.mode}`;
    lanesOf.set(key, (lanesOf.get(key) ?? 0) + 1);
  }

  const groups = new Map<string, NoticeGroup>();
  for (const row of search.vendors) {
    const kind = kindOf(row);
    if (!kind) continue;
    // a lane without rates and a lane the vendor does not serve read the same, so they share one line
    const noRates = kind === 'no_rates' || kind === 'refused';
    const key = noRates
      ? [row.vendor, row.mode, 'no_rates'].join('|')
      : [row.vendor, row.mode, row.status, kind, row.message].join('|');
    const group = groups.get(key) ?? {
      key,
      vendor: row.vendor,
      vendorName: row.display_name,
      mode: row.mode,
      status: row.status,
      kind,
      message: noRates ? null : row.message,
      routes: [],
      reasons: [],
      laneTotal: lanesOf.get(`${row.vendor}|${row.mode}`) ?? 1,
      retryIds: [],
      rated: quoteCounts.has(`${row.vendor}|${row.mode}`),
    };
    if (kind === 'refused') group.kind = 'refused';
    const route = routeText(row);
    // a refusal with no airport or port route (road) has nowhere else to say why
    if (kind === 'refused' && !route && row.message) {
      const said = group.message?.split('; ') ?? [];
      if (!said.includes(row.message)) said.push(row.message);
      group.message = said.join('; ');
    }
    if (route) {
      group.routes.push(route);
      group.reasons.push(kind === 'refused' ? row.message : null);
    }
    if (retryable(row)) group.retryIds.push(row.id);
    groups.set(key, group);
  }

  const sources: RateSource[] = [];
  const seen = new Set<string>();
  for (const row of search.vendors) {
    const key = `${row.vendor}|${row.mode}`;
    const count = quoteCounts.get(key);
    if (count && !seen.has(key)) {
      seen.add(key);
      sources.push({
        label: sourceLabel(row.display_name, row.mode),
        count,
      });
    }
  }

  const warnings = new Set<string>();
  for (const row of search.vendors) {
    for (const text of row.warnings) {
      warnings.add(
        text.toLowerCase().startsWith(row.display_name.toLowerCase())
          ? text
          : `${row.display_name}: ${text}`
      );
    }
  }
  return { sources, groups: [...groups.values()], warnings: [...warnings] };
};

const where = (group: NoticeGroup): string => {
  if (group.routes.length === 0) return '';
  if (group.laneTotal <= 1) return ` for ${group.routes[0]}`;
  return ` on ${group.routes.length} of ${group.laneTotal} routes`;
};

/** The sentence a group of rows is shown as, in plain words. */
export const describeGroup = (group: NoticeGroup): string => {
  const who = sourceLabel(group.vendorName, group.mode);
  const reason = group.message ? `: ${group.message}` : '.';
  switch (group.kind) {
    case 'pending':
      return `Still collecting from ${who}…`;
    case 'credentials':
      return `${who} was not searched. ${group.message ?? 'The saved login was refused.'}`;
    case 'not_connected':
      return `${who} was not searched: it is not connected. Ask an admin to connect it in Settings > Transport providers.`;
    case 'unsupported':
      return `${who} was not searched${reason}`;
    case 'refused':
      return group.routes.length === 0 && group.message
        ? `${who} had no rates: ${group.message}`
        : `${who} had no rates${where(group)}.`;
    case 'no_rates':
      return `${who} had no rates${where(group)}.`;
    case 'timeout':
      return `${who} did not answer in time${where(group)}.`;
    case 'unavailable':
      return `${who} is unavailable right now${where(group)}${reason}`;
    case 'error':
      return `${who} could not be searched${where(group)}${reason}`;
  }
};

/** Groups worth a red banner: failures. A lane a vendor does not serve is an expected "no rates", not an error. */
export const isLoud = (group: NoticeGroup): boolean =>
  ['timeout', 'unavailable', 'error', 'credentials'].includes(group.kind);

const NOT_SERVED = /not served:\s*(.+)$/i;

/** The airports or ports a vendor said it does not serve, from "Origin not served: MKE" style reasons. */
export const unservedSites = (group: NoticeGroup): string[] => {
  const codes = group.reasons.flatMap((reason) => {
    const named = reason?.match(NOT_SERVED)?.[1];
    return named ? named.split(',').map((code) => code.trim()) : [];
  });
  return [...new Set(codes.filter(Boolean))];
};

/** "Airports not served: MKE, MDW", or an empty string when no reason names a site. */
export const unservedText = (group: NoticeGroup): string => {
  const sites = unservedSites(group);
  const noun = group.mode === 'ocean' ? 'Ports' : 'Airports';
  return sites.length ? `${noun} not served: ${sites.join(', ')}` : '';
};

/** The routes of a group, with the reason beside a route only when it does not name a site. */
export const routeLines = (group: NoticeGroup): string =>
  group.routes
    .map((route, i) =>
      group.reasons[i] && !NOT_SERVED.test(group.reasons[i])
        ? `${route} (${group.reasons[i]})`
        : route
    )
    .join(', ');

export const hasReasons = (group: NoticeGroup): boolean =>
  group.reasons.some(Boolean);

/** Groups that need someone to act (fix a login, try again): they stay visible; the expected skips fold away. */
export const needsAction = (group: NoticeGroup): boolean =>
  ['credentials', 'not_connected', 'timeout', 'unavailable', 'error'].includes(
    group.kind
  );

/** One line for the rows still waiting, instead of a banner each. */
export const pendingText = (report: SearchReport): string => {
  const who = [
    ...new Set(
      report.groups
        .filter((g) => g.kind === 'pending')
        .map((g) => sourceLabel(g.vendorName, g.mode))
    ),
  ];
  return who.length ? `Still collecting from ${who.join(', ')}…` : '';
};

/** One line saying where the rates came from and what did not deliver. */
export const summaryText = (
  report: SearchReport,
  quoteCount: number
): string => {
  const parts: string[] = [];
  if (quoteCount > 0) {
    parts.push(
      `${quoteCount} rate${quoteCount === 1 ? '' : 's'} from ${report.sources
        .map((s) => `${s.label} ${s.count}`)
        .join(', ')}`
    );
  }
  const empty = report.groups.filter(
    (g) => g.kind === 'no_rates' || g.kind === 'refused'
  );
  const labels = (rated: boolean): string[] =>
    empty
      .filter((g) => g.rated === rated)
      .map((g) => sourceLabel(g.vendorName, g.mode));
  const none = labels(false);
  const some = labels(true);
  if (none.length) parts.push(`no rates from ${none.join(', ')}`);
  if (some.length) {
    parts.push(`${some.join(', ')} had no rates on some routes`);
  }
  const failed = report.groups.filter((g) => isLoud(g));
  if (failed.length) {
    const lanes = failed.reduce(
      (sum, g) => sum + Math.max(g.routes.length, 1),
      0
    );
    parts.push(`${lanes} search${lanes === 1 ? '' : 'es'} did not return`);
  }
  return parts.join(' · ');
};

export interface EmptyExplanation {
  title: string;
  hint: string;
}

/** Why nothing came back, built from what each vendor said (not a fixed sentence). */
export const explainEmpty = (report: SearchReport): EmptyExplanation => {
  const kinds = new Set(report.groups.map((g) => g.kind));
  const searched = report.groups.some((g) =>
    ['no_rates', 'refused', 'timeout', 'unavailable', 'error'].includes(g.kind)
  );
  if (!searched && report.groups.length > 0) {
    return {
      title: 'No vendor could search this lane.',
      hint:
        kinds.has('credentials') || kinds.has('not_connected')
          ? 'A vendor needs to be connected or reconnected. An admin can do this in Settings > Transport providers.'
          : 'None of the connected vendors quote this mode or cargo. The reasons are below.',
    };
  }
  if (kinds.has('timeout') || kinds.has('unavailable') || kinds.has('error')) {
    return {
      title: 'Some searches did not finish, and the rest had no rates.',
      hint: 'Retry the ones that failed, or try again in a few minutes.',
    };
  }
  if (kinds.has('refused')) {
    const roadOnly = report.groups
      .filter((g) => g.kind === 'refused')
      .every((g) => g.mode === 'road');
    return {
      title: 'The vendors do not serve this lane as searched.',
      hint: roadOnly
        ? 'Check the road locations and cargo; the reason is shown above.'
        : 'Try another nearby airport or port, or a different ship date.',
    };
  }
  return {
    title: 'No rates found for this lane and date.',
    hint: 'Try another ship date or a wider flexibility, a nearby airport or port, or different cargo.',
  };
};
